import { yupResolver } from '@hookform/resolvers/yup';
import { BlogFont, BlogHomeCustomization, IOperationResultNoValue } from '@ViewModels';
import { css } from 'aphrodite';
import equal from 'fast-deep-equal';
import { FC, useEffect, useState } from 'react';
import { FieldErrors, SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useImageAttachmentMutation } from '../../../../../queries/Attachments/useImageAttachmentMutation';
import { useFindOrCreateAndUpdateBlogStyleMutation } from '../../../../../queries/CustomPageStyle/useFindOrCreateAndUpdateBlogStyleMutation';
import { Button } from '../../../../../web/components-v2/Button';
import { ColorPicker } from '../../../../../web/components-v2/ColorPicker';
import { ImageFileChooserModal } from '../../../../../web/components/ImageFileChooserModal';
import { Toggle } from '../../../../../web/components/Toggle';
import { TrashIcon } from '../../../../../web/components/svgs/icons/TrashIcon';
import { bs } from '../../../../../web/styles/styles';
import { useAccountContext } from '../../../../components/blogs';
import { styleSheet } from '../../../../components/blogs/styles';
import { FontSelect } from '../../../../components/FontSelect/FontSelect';
import { InfoPopover } from '../../../../components/InfoPopover/InfoPopover';
import { LabeledInput } from '../../../../components/LabeledInput/LabeledInput';
import { Column, Row } from '../../../../components/Layout';
import { DEFAULT_FONT_VALUES, DEFAULT_HOME_FORM_VALUES } from './constants';
import { blogHomeSchema } from './schemas';
import { TemplateFormWrapper } from './TemplateFormWrapper';

interface BlogHomeDesignFormProps {
	customContent?: BlogHomeCustomization;
	onUpdateSuccess: () => void;
	onFormDirty: () => void;
}

export const HomeDesignForm: FC<BlogHomeDesignFormProps> = ({ customContent, onFormDirty, onUpdateSuccess }) => {
	const errorMessages = useErrorMessages();
	const [imageModalOpen, setImageModalOpen] = useState<boolean>(false);
	const { accountId, impersonationContext } = useAccountContext();

	const {
		handleSubmit,
		register,
		setValue,
		getValues,
		watch,
		formState: { errors, isDirty },
	} = useForm<BlogHomeCustomization>({
		defaultValues: DEFAULT_HOME_FORM_VALUES,
		mode: 'onBlur',
		resolver: yupResolver(blogHomeSchema),
	});
	const metadataToggle = watch('cardStyle.showAuthorMetaData');
	const nookToggle = watch('hideNook');
	const font = watch('font', DEFAULT_FONT_VALUES);

	// Watch color values for ColorPicker components
	const pageBackgroundColor = watch('pageBackgroundColor');
	const blogTitleColor = watch('blogTitle.color');
	const cardTitleColor = watch('cardStyle.titleStyle.color');
	const cardLinkColor = watch('cardStyle.linkStyle.color');

	const handleToggleMetadata = (value: boolean) => {
		setValue('cardStyle.showAuthorMetaData', value);
	};
	const handleNookToggle = (value: boolean) => {
		setValue('hideNook', value);
	};
	const handleSelectFont = (selectedFont: BlogFont) => {
		setValue('font', selectedFont);
	};

	const updateHomeMutation = useFindOrCreateAndUpdateBlogStyleMutation({
		onSuccess: () => {
			onUpdateSuccess();
			toast.success('Saved changes successfully');
		},
		onError: (error: IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	const onValid: SubmitHandler<BlogHomeCustomization> = (data, event) => {
		event?.preventDefault();
		event?.stopPropagation();

		updateHomeMutation.mutate({
			id: accountId,
			impersonationContext,
			style: data,
		});
	};

	const onInvalid = (validationErrors: FieldErrors<BlogHomeCustomization>) => {
		const validationErrorMessages = Object.entries(validationErrors)
			.map(([field, error]) => `${field}: ${error?.message || 'invalid'}`)
			.join(', ');
		toast.error(`Validation failed: ${validationErrorMessages}`);
	};

	const imageUpload = useImageAttachmentMutation({
		onError: error => errorMessages.pushApiError(error, 'Failed to upload image'),
		onSuccess: result => {
			setValue('headerImage', result.at(0));
		},
	});

	// Sync customContent to form values when it changes (like HeaderDesignForm)
	useEffect(() => {
		if (customContent && !equal(customContent, getValues())) {
			Object.entries(customContent).forEach(([key, value]) => {
				if (value !== undefined && value !== null) {
					setValue(key as keyof BlogHomeCustomization, value);
				}
			});
		}
	}, [customContent, getValues, setValue]);

	useEffect(() => {
		if (isDirty) {
			onFormDirty();
		}
	}, [isDirty, onFormDirty]);

	return (
		<>
			<TemplateFormWrapper
				title='Customize Blog Home Page'
				isLoading={updateHomeMutation.isPending}
				errors={errors}
				onSubmit={handleSubmit(onValid, onInvalid)}
			>
				<Row className={css(styleSheet.gap24)}>
					<Column className={css(bs.flex1, styleSheet.gap24)}>
						<h2 className={css(styleSheet.subtitleText, bs.flex1)}>Background</h2>
						<div className={css(styleSheet.gap8)}>
							<label className={css(styleSheet.label)}>Page Background Color</label>
							<ColorPicker
								value={pageBackgroundColor || '#FFFFFF'}
								onChange={color => setValue('pageBackgroundColor', color, { shouldDirty: true })}
							/>
						</div>
						<Column>
							<div className={css(bs.mb4)}>
								<label className={css(styleSheet.label, styleSheet.labelRow)} htmlFor='upload-header-button'>
									Header Image
								</label>
								<Button variant='secondary' id='upload-header-button' onClick={() => setImageModalOpen(true)}>
									Upload Header Image
								</Button>
							</div>

							{watch('headerImage')?.id && (
								<div>
									<label className={css(styleSheet.label, styleSheet.labelRow)} htmlFor='remove-header-button'>
										Remove Header Image
									</label>
									<Button
										variant='destructive'
										id='remove-header-button'
										onClick={() => setValue('headerImage', { fileName: '', fileSize: 0, id: '', url: '' })}
									>
										<TrashIcon /> Remove Header Image
									</Button>
								</div>
							)}
						</Column>
					</Column>
					<Column className={css(bs.flex1, styleSheet.gap24)}>
						<h2 className={css(styleSheet.subtitleText, bs.flex1)}>Font</h2>
						<FontSelect
							initialValue={customContent?.font}
							onChange={handleSelectFont}
							errors={errors?.font}
							value={font}
						/>
					</Column>
					<Column className={css(bs.flex1, styleSheet.gap24)}>
						<h2 className={css(styleSheet.subtitleText, bs.flex1)}>Section Title</h2>
						<LabeledInput error={errors?.blogTitle?.title} label='Title Text' {...register('blogTitle.title')} />
						<div className={css(styleSheet.gap8)}>
							<label className={css(styleSheet.label)}>Color</label>
							<ColorPicker
								value={blogTitleColor || '#000000'}
								onChange={color => setValue('blogTitle.color', color, { shouldDirty: true })}
							/>
						</div>
						<Column>
							<Row className={css(bs.itemsCenter, styleSheet.labelRow)}>
								<label htmlFor='nook-toggle' className={css(styleSheet.label)}>
									Hide Nook
								</label>
								<InfoPopover message='This setting will be used for the blog home page, as well as individual posts' />
							</Row>
							<Toggle id='nook-toggle' isOn={nookToggle} onToggleCheckChanged={handleNookToggle} />
						</Column>
					</Column>
					<Column className={css(bs.flex1, styleSheet.gap24)}>
						<h2 className={css(styleSheet.subtitleText, bs.flex1)}>Individual Post</h2>
						<div className={css(styleSheet.gap8)}>
							<label className={css(styleSheet.label)}>Title Color</label>
							<ColorPicker
								value={cardTitleColor || '#000000'}
								onChange={color => setValue('cardStyle.titleStyle.color', color, { shouldDirty: true })}
							/>
						</div>
						<div className={css(styleSheet.gap8)}>
							<label className={css(styleSheet.label)}>Link Color</label>
							<ColorPicker
								value={cardLinkColor || '#599EED'}
								onChange={color => setValue('cardStyle.linkStyle.color', color, { shouldDirty: true })}
							/>
						</div>
						<Column>
							<label htmlFor='metadata-toggle' className={css(styleSheet.label, styleSheet.labelRow)}>
								Show Author Metadata
							</label>
							<Toggle id='metadata-toggle' isOn={metadataToggle} onToggleCheckChanged={handleToggleMetadata} />
						</Column>
					</Column>
					<Column className={css(bs.flex1)} />
				</Row>
			</TemplateFormWrapper>
			<ImageFileChooserModal
				title='Upload Header Image'
				modalProps={{
					isOpen: imageModalOpen,
					onRequestClose: (result, cancel) => {
						if (!cancel && result) imageUpload.mutate({ files: [result], impersonationContext });
						setImageModalOpen(false);
					},
				}}
			/>
		</>
	);
};
